import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import type { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import DOMPurify from 'dompurify';
import { twMerge } from 'tailwind-merge';
import './RichTextEditor.css';

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  editable?: boolean;
}

const MenuBar: React.FC<{ editor: Editor | null }> = ({ editor }) => {
  if (!editor) return null;

  const btnClass = (active: boolean) => twMerge(
    'py-1.5 px-2.5 border border-border-medium rounded bg-bg-primary text-text-primary cursor-pointer text-sm min-w-[32px] transition-all',
    'hover:bg-bg-tertiary',
    active && 'bg-aegean text-white border-aegean',
  );

  return (
    <div className="flex flex-wrap gap-1 p-2 bg-bg-secondary border-b border-border-medium">
      <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={btnClass(editor.isActive('bold'))} title="Bold"><strong>B</strong></button>
      <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={btnClass(editor.isActive('italic'))} title="Italic"><em>I</em></button>
      <button type="button" onClick={() => editor.chain().focus().toggleStrike().run()} className={btnClass(editor.isActive('strike'))} title="Strikethrough"><s>S</s></button>
      <span className="w-px bg-border-medium mx-1 self-stretch" />
      <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={btnClass(editor.isActive('bulletList'))} title="Bullet List">{'\u2022'} List</button>
      <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btnClass(editor.isActive('orderedList'))} title="Numbered List">1. List</button>
      <span className="w-px bg-border-medium mx-1 self-stretch" />
      <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={btnClass(editor.isActive('heading', { level: 3 }))} title="Heading">H</button>
      <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()} className={btnClass(editor.isActive('blockquote'))} title="Quote">&ldquo;</button>
      <button type="button" onClick={() => editor.chain().focus().toggleCodeBlock().run()} className={btnClass(editor.isActive('codeBlock'))} title="Code Block">{'</>'}</button>
    </div>
  );
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({ content, onChange, placeholder = 'Enter details...', editable = true }) => {
  const editor = useEditor({
    extensions: [StarterKit, Placeholder.configure({ placeholder })],
    content, editable,
    // TIPTAP-NULLCACHE (bug-registry.md): onUpdate can be flushed by React's
    // scheduler after the editor has already been destroy()'d -- destroy()
    // nulls `editor.schema`, and getHTML() reading it throws
    // "Cannot read properties of null (reading 'cached')". `isDestroyed` is
    // the library's own guard for exactly this post-destroy callback race.
    onUpdate: ({ editor }) => {
      if (editor.isDestroyed) return;
      onChange(editor.getHTML());
    },
  });

  React.useEffect(() => {
    if (editor && !editor.isDestroyed && content !== editor.getHTML()) editor.commands.setContent(content);
  }, [content, editor]);

  return (
    <div className={twMerge(
      'border border-border-light rounded-lg overflow-hidden bg-surface',
      'focus-within:border-accent focus-within:ring-2 focus-within:ring-primary-light',
      !editable && 'border-none bg-transparent',
    )}>
      {editable && <MenuBar editor={editor} />}
      <EditorContent editor={editor} className="editor-content p-3 min-h-[150px] max-h-[400px] overflow-y-auto bg-surface" />
    </div>
  );
};

export const RichTextDisplay: React.FC<{ content: string }> = ({ content }) => {
  if (!content) return null;
  const sanitizedContent = DOMPurify.sanitize(content, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'a', 'span', 'div'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
    ALLOW_DATA_ATTR: false,
  });
  return <div className="rich-text-display" dangerouslySetInnerHTML={{ __html: sanitizedContent }} />;
};

export default RichTextEditor;
